import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowLeft, Barbell, TrendUp } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useWeightUnit } from "@/lib/units";
import ExerciseDemoButton from "@/components/ExerciseDemoButton";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1C1C1C] border border-[#27272A] p-3 text-sm">
      <p className="text-[#A1A1AA] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.stroke }} className="font-semibold">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

export default function WorkoutDetail({ workout, api, onBack }) {
  const { fromKg, label: weightLabel, formatWeight } = useWeightUnit();
  const [exerciseProgress, setExerciseProgress] = useState({});
  const [loadingProgress, setLoadingProgress] = useState({});

  const fetchProgress = async (exerciseName) => {
    if (exerciseProgress[exerciseName] || loadingProgress[exerciseName]) return;
    setLoadingProgress((prev) => ({ ...prev, [exerciseName]: true }));
    try {
      const res = await axios.get(`${api}/workouts/exercise-progress`, {
        params: { name: exerciseName },
      });
      setExerciseProgress((prev) => ({ ...prev, [exerciseName]: res.data.progress || [] }));
    } catch (e) {
      console.error("Progress fetch error:", e);
    } finally {
      setLoadingProgress((prev) => ({ ...prev, [exerciseName]: false }));
    }
  };

  useEffect(() => {
    if (workout?.exercises) {
      workout.exercises.forEach((ex) => {
        if (ex.name) fetchProgress(ex.name);
      });
    }
  }, [workout]);

  if (!workout) return null;

  const totalVolume = workout.exercises?.reduce((sum, ex) => {
    return sum + (ex.sets?.reduce((s, set) => s + set.reps * set.weight, 0) || 0);
  }, 0) || 0;

  const totalSets = workout.exercises?.reduce((sum, ex) => sum + (ex.sets?.length || 0), 0) || 0;

  return (
    <div className="space-y-6" data-testid="workout-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="text-[#A1A1AA] hover:text-white transition-colors p-2 border border-[#27272A] hover:border-[#3f3f46]"
          data-testid="workout-detail-back-btn"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            {workout.name}
          </h2>
          <div className="flex items-center gap-4 mt-1">
            <span className="text-sm text-[#71717A]">{workout.date}</span>
            {workout.duration_minutes > 0 && (
              <span className="text-sm text-[#A1A1AA]">{workout.duration_minutes} min</span>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#161616] border border-[#27272A] p-4 text-center">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1">Exercises</p>
          <p className="text-2xl font-black text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {workout.exercises?.length || 0}
          </p>
        </div>
        <div className="bg-[#161616] border border-[#27272A] p-4 text-center">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1">Total Sets</p>
          <p className="text-2xl font-black text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {totalSets}
          </p>
        </div>
        <div className="bg-[#161616] border border-[#27272A] p-4 text-center">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1">Volume</p>
          <p className="text-2xl font-black text-[#E05A1D]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {fromKg(totalVolume).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
          <p className="text-xs text-[#71717A]">{weightLabel}</p>
        </div>
      </div>

      {/* Notes */}
      {workout.notes && (
        <div className="bg-[#161616] border border-[#27272A] p-4">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1">Notes</p>
          <p className="text-sm text-[#A1A1AA]">{workout.notes}</p>
        </div>
      )}

      {/* Exercises with Progression Charts */}
      <div className="space-y-4" data-testid="exercise-details">
        {workout.exercises?.map((ex, exIdx) => {
          const progressKg = exerciseProgress[ex.name] || [];
          const progress = progressKg.map((p) => ({
            ...p,
            max_weight: Number(fromKg(p.max_weight).toFixed(1)),
            total_volume: Number(fromKg(p.total_volume).toFixed(1)),
          }));
          const hasChart = progress.length > 1;

          return (
            <div
              key={exIdx}
              className="bg-[#161616] border border-[#27272A] p-5"
              data-testid={`detail-exercise-${exIdx}`}
            >
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Barbell size={16} className="text-[#E05A1D]" weight="bold" />
                <h3
                  className="text-base font-bold tracking-tight uppercase text-white"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {ex.name}
                </h3>
                {ex.name && <ExerciseDemoButton api={api} name={ex.name} />}
              </div>

              {/* Sets Table */}
              <div className="mb-4">
                <div className="grid grid-cols-[1fr_2fr_2fr_2fr] gap-2 text-xs text-[#71717A] font-bold uppercase tracking-wider mb-2 px-1">
                  <span>Set</span><span>Reps</span><span>Weight ({weightLabel})</span><span>Volume</span>
                </div>
                {ex.sets?.map((set, sIdx) => (
                  <div
                    key={sIdx}
                    className="grid grid-cols-[1fr_2fr_2fr_2fr] gap-2 items-center py-1.5 border-b border-[#27272A] last:border-0"
                  >
                    <span className="text-xs text-[#A1A1AA] text-center">{sIdx + 1}</span>
                    <span className="text-sm text-white">{set.reps}</span>
                    <span className="text-sm text-white">{formatWeight(set.weight, { decimals: 1, withUnit: false })}</span>
                    <span className="text-sm text-[#E05A1D] font-semibold">{fromKg(set.reps * set.weight).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>

              {/* Progression Chart */}
              {hasChart && (
                <div data-testid={`progress-chart-${ex.name}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendUp size={14} className="text-[#22C55E]" weight="bold" />
                    <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]">
                      Weight Progression
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={progress}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                      <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 10 }} />
                      <YAxis tick={{ fill: '#71717A', fontSize: 10 }} domain={['auto', 'auto']} />
                      <Tooltip content={<ChartTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="max_weight"
                        stroke="#E05A1D"
                        strokeWidth={2}
                        dot={{ fill: '#E05A1D', r: 3 }}
                        name={`Max Weight (${weightLabel})`}
                      />
                      <Line
                        type="monotone"
                        dataKey="total_volume"
                        stroke="#22C55E"
                        strokeWidth={1.5}
                        dot={{ fill: '#22C55E', r: 2 }}
                        strokeDasharray="5 5"
                        name="Volume"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {!hasChart && progress.length <= 1 && (
                <p className="text-xs text-[#71717A] italic">
                  {progress.length === 1
                    ? "Log this exercise again to see progression"
                    : ""}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
